import { useCallback, useMemo, useState } from 'react';
import { HiOutlineEye } from 'react-icons/hi';
import { Link, useLocation } from 'react-router-dom';
import { ActionIcon, Button, Text, Tooltip } from 'rizzui';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import CustomTable from '../../../components/table/CustomTable';
import useFetch from '../../../hooks/useFetch';
import Page from '../../../layout/Page/Page';
import { menuRoutes } from '../../../router/menu';
import { IncidenceService } from '../../../services/incidence/incidenceService';
import { ContractProfileLocationState } from '../../../utils/contractNavigation';
import TutorIncidenceResolveModal from './TutorIncidenceResolveModal';

type ResolutionStatusTab = 'pending' | 'resolved' | 'unresolved';

const RESOLUTION_TABS: { label: string; value: ResolutionStatusTab }[] = [
    { label: 'Pendientes', value: 'pending' },
    { label: 'Resueltas', value: 'resolved' },
    { label: 'No resueltas', value: 'unresolved' },
];

const TutorIncidencesList = () => {
    const service = new IncidenceService();
    const location = useLocation();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, setPage } = useFiltersPR();
    const { userCan } = usePrivilege();
    const [selectedIncidence, setSelectedIncidence] = useState<any | null>(null);
    const [isResolveModalOpen, setIsResolveModalOpen] = useState(false);
    const activeTab = (filters.filter_filters?.resolution_status as ResolutionStatusTab | undefined) ?? 'pending';
    const returnToTutorIncidencesPath = `${location.pathname}${location.search}`;

    const [response, loading, _error, refetch] = useFetch(useCallback(async () => {
        const apiResponse = await service.listTutorIncidences(filters);
        return apiResponse.getResponseData();
    }, [filters]));

    const rows = response?.data ?? [];
    const data = useMemo(() => rows.map((row: any) => ({
        id: row.id,
        code: row.code,
        subject: row.subject,
        manager: row.manager,
        contract: row.contract,
        currentStatus: row.currentStatus,
        currentStatusDate: row.currentStatusDate,
        updatedAt: row.updatedAt,
        createdAt: row.createdAt,
        raw: row,
    })), [rows]);

    const handleTabChange = (tab: ResolutionStatusTab) => {
        updateFilters({ resolution_status: tab });
        setPage(1);
    };

    const openResolveModal = (row: any) => {
        setSelectedIncidence(row.raw);
        setIsResolveModalOpen(true);
    };

    const canViewContract = userCan('get_contracts', 'contracts');
    const canResolveTutorIncidences = userCan('show_all_tutor_incidences', 'incidences');

    return (
        <Page container="fluid">
            <div className="mb-4 flex gap-1 overflow-x-auto border-b border-muted">
                {RESOLUTION_TABS.map((tab) => (
                    <button
                        key={tab.value}
                        type="button"
                        onClick={() => handleTabChange(tab.value)}
                        className={`whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                            activeTab === tab.value
                                ? 'border-primary text-primary'
                                : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {response !== undefined && (
                <CustomTable
                    id="tutor-incidences-table"
                    data={data}
                    columnOrder={['code', 'subject', 'manager', 'contract', 'currentStatus', 'updatedAt', 'createdAt']}
                    isLoading={loading}
                    columnsNotShown={['id', 'currentStatusDate', 'raw']}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: response?.lastPage || 1,
                        totalCount: response?.totalRegisters || 0,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    overrideColumns={[
                        {
                            key: 'manager',
                            render: (row: any) => {
                                const manager = row.row.original.manager;
                                return manager ? `${manager.name || ''} ${manager.lastName || ''}`.trim() : 'Sin asignar';
                            }
                        },
                        {
                            key: 'contract',
                            render: (row: any) => {
                                const contract = row.row.original.contract;
                                if (!contract?.id || !contract?.code) {
                                    return 'N/A';
                                }

                            if (!canViewContract) {
                                return contract.code;
                            }

                            const contractProfileState: ContractProfileLocationState = {
                                fromTutorIncidences: true,
                                returnToTutorIncidencesPath,
                            };

                            return (
                                <Link
                                    to={`${menuRoutes.contracts.path}/${contract.id}/profile${menuRoutes.contracts.profile.info}`}
                                    state={contractProfileState}
                                >
                                    <Text className="font-bold text-primary">{contract.code}</Text>
                                </Link>
                            );
                            }
                        },
                        {
                            key: 'currentStatus',
                            render: (row: any) => row.row.original.currentStatus?.name || 'N/A'
                        },
                        {
                            key: 'updatedAt',
                            render: (row: any) => row.row.original.updatedAt?.date || row.row.original.updatedAt || '-'
                        },
                        {
                            key: 'createdAt',
                            render: (row: any) => row.row.original.createdAt?.date || row.row.original.createdAt || '-'
                        },
                    ]}
                    actions={[
                        {
                            label: 'View',
                            permissions: { group: 'incidences', permission: 'get_incidences' },
                            render: (row: any) => (
                                <Tooltip size="sm" content="Ver incidencia" placement="top" color="invert">
                                    <Link to={`${menuRoutes.incidences.path}/${row.id}/profile${menuRoutes.incidences.profile.info}`}>
                                        <ActionIcon as="span" size="sm" variant="outline" className="hover:!border-gray-900 hover:text-gray-700">
                                            <HiOutlineEye className="h-4 w-4" size={20} />
                                        </ActionIcon>
                                    </Link>
                                </Tooltip>
                            ),
                        },
                        {
                            label: 'Resolve',
                            permissions: { group: 'incidences', permission: 'show_all_tutor_incidences' },
                            render: (row: any) => {
                                const currentStatus = row.raw?.currentStatus;
                                const isResolved = activeTab !== 'pending'
                                    || currentStatus?.satisfactoryStatus === true
                                    || currentStatus?.unsatisfactoryStatus === true;

                                if (isResolved) {
                                    return null;
                                }

                                return (
                                    <Button size="sm" variant="outline" onClick={() => openResolveModal(row)}>
                                        Resolver
                                    </Button>
                                );
                            },
                        },
                    ]}
                    search
                    filter={false}
                    viewFilters={false}
                />
            )}

            {!response && loading && (
                <Loader height="40vh" />
            )}

            {canResolveTutorIncidences && (
                <TutorIncidenceResolveModal
                    incidence={selectedIncidence}
                    isOpen={isResolveModalOpen}
                    onClose={() => {
                        setIsResolveModalOpen(false);
                        setSelectedIncidence(null);
                    }}
                    refetch={refetch}
                />
            )}
        </Page>
    );
};

export default TutorIncidencesList;
